import { ComponentType } from 'react';

import React from 'react';

/**
 * Feature registry for dynamic loading
 */
interface FeatureModule {
  default: ComponentType<any>;
  preload?: () => Promise<void>;
  dependencies?: string[];
}

interface FeatureConfig {
  name: string;
  loader: () => Promise<FeatureModule>;
  preloadCondition?: () => boolean;
  dependencies?: string[];
  priority?: 'low' | 'medium' | 'high';
}

class DynamicFeatureLoader {
  private loadedFeatures = new Map<string, FeatureModule>();
  private loadingPromises = new Map<string, Promise<FeatureModule>>();
  private featureRegistry = new Map<string, FeatureConfig>();
  private preloadQueue: string[] = [];

  /**
   * Register a feature for dynamic loading
   */
  registerFeature(config: FeatureConfig): void {
    this.featureRegistry.set(config.name, config);
    
    // Add to preload queue if condition is met
    if (config.preloadCondition?.()) {
      this.schedulePreload(config.name, config.priority || 'low');
    }
  }

  /**
   * Load a feature dynamically
   */
  async loadFeature(featureName: string): Promise<FeatureModule> {
    // Return cached feature if already loaded
    if (this.loadedFeatures.has(featureName)) {
      return this.loadedFeatures.get(featureName)!;
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(featureName)) {
      return this.loadingPromises.get(featureName)!;
    }

    const config = this.featureRegistry.get(featureName);
    if (!config) {
      throw new Error(`Feature '${featureName}' not registered`);
    }

    // Load dependencies first
    if (config.dependencies?.length) {
      await Promise.all(
        config.dependencies.map(dep => this.loadFeature(dep))
      );
    }

    // Start loading the feature
    const loadingPromise = this.loadFeatureModule(config);
    this.loadingPromises.set(featureName, loadingPromise);

    try {
      const module = await loadingPromise;
      this.loadedFeatures.set(featureName, module);
      this.loadingPromises.delete(featureName);
      
      // Run preload if available
      if (module.preload) {
        await module.preload();
      }
      
      return module;
    } catch (error) {
      this.loadingPromises.delete(featureName);
      throw error;
    }
  }

  /**
   * Preload features based on priority and conditions
   */
  async preloadFeatures(): Promise<void> {
    const highPriorityFeatures = this.getFeaturesByPriority('high');
    const mediumPriorityFeatures = this.getFeaturesByPriority('medium');

    // Load high priority features immediately
    await Promise.all(
      highPriorityFeatures.map(name => this.loadFeature(name).catch(console.warn))
    );

    // Load medium priority features with delay
    setTimeout(() => {
      Promise.all(
        mediumPriorityFeatures.map(name => this.loadFeature(name).catch(console.warn))
      );
    }, 2000);

    // Load low priority features when idle
    this.scheduleIdlePreload();
  }

  /**
   * Check if a feature is loaded
   */
  isFeatureLoaded(featureName: string): boolean {
    return this.loadedFeatures.has(featureName);
  }

  /**
   * Get loading status of a feature
   */
  isFeatureLoading(featureName: string): boolean {
    return this.loadingPromises.has(featureName);
  }

  /**
   * Preload feature on user interaction (hover, focus, etc.)
   */
  preloadOnInteraction(featureName: string, element: HTMLElement): void {
    let preloaded = false;
    
    const preload = () => {
      if (!preloaded && !this.isFeatureLoaded(featureName)) {
        preloaded = true;
        this.loadFeature(featureName).catch(console.warn);
      }
    };

    // Preload on hover or focus
    element.addEventListener('mouseenter', preload, { once: true });
    element.addEventListener('focus', preload, { once: true });
    
    // Also preload on touch start for mobile
    element.addEventListener('touchstart', preload, { once: true });
  }

  /**
   * Get a loaded feature if available
   */
  getLoadedFeature(featureName: string): FeatureModule | null {
    return this.loadedFeatures.get(featureName) || null;
  }
  getBundleInfo(): { loaded: string[]; loading: string[]; registered: string[] } {
    return {
      loaded: Array.from(this.loadedFeatures.keys()),
      loading: Array.from(this.loadingPromises.keys()),
      registered: Array.from(this.featureRegistry.keys())
    };
  }

  private async loadFeatureModule(config: FeatureConfig): Promise<FeatureModule> {
    try {
      return await config.loader();
    } catch (error) {
      console.error(`Failed to load feature '${config.name}':`, error);
      throw error;
    }
  }

  private getFeaturesByPriority(priority: 'low' | 'medium' | 'high'): string[] {
    return Array.from(this.featureRegistry.entries())
      .filter(([, config]) => config.priority === priority)
      .map(([name]) => name);
  }

  private schedulePreload(featureName: string, priority: 'low' | 'medium' | 'high'): void {
    if (priority === 'high') {
      this.preloadQueue.unshift(featureName);
    } else {
      this.preloadQueue.push(featureName);
    }
  }

  private scheduleIdlePreload(): void {
    if ('requestIdleCallback' in window) {
      const preloadNext = () => {
        if (this.preloadQueue.length > 0) {
          const featureName = this.preloadQueue.shift()!;
          this.loadFeature(featureName).catch(console.warn);
          
          // Schedule next preload
          if (this.preloadQueue.length > 0) {
            requestIdleCallback(preloadNext);
          }
        }
      };
      
      requestIdleCallback(preloadNext);
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        const lowPriorityFeatures = this.getFeaturesByPriority('low');
        Promise.all(
          lowPriorityFeatures.map(name => this.loadFeature(name).catch(console.warn))
        );
      }, 5000);
    }
  }
}

// Global feature loader instance
export const featureLoader = new DynamicFeatureLoader();

/**
 * Hook for using dynamic features in components
 */
export function useDynamicFeature(featureName: string) {
  const [feature, setFeature] = React.useState<FeatureModule | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (featureLoader.isFeatureLoaded(featureName)) {
      const loadedFeature = featureLoader.getLoadedFeature(featureName);
      if (loadedFeature) {
        setFeature(loadedFeature);
      }
      return;
    }

    setLoading(true);
    setError(null);

    featureLoader.loadFeature(featureName)
      .then(module => {
        setFeature(module);
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  }, [featureName]);

  return { feature, loading, error };
}